import { useMemo } from 'react'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'
import { Badge, Box, Text } from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'

import structure from '@/structure'
import { colors } from '@/styles'
import { getImageUrl } from '@/utils/image'
import useFancybox from '@/hooks/use-fancybox'
import { useVideoThumbnail } from '@/hooks/use-video-thumbnail'
import useTourLinks from '@/features/ListingDetail/use-tour-links'
import { getListingTypeText } from '@/components/listing-card-new/utils/listingTypeText'

import videoPlaceholder from './video-placeholder.png'

const CarouselLightboxButton = styled.button`
  background: transparent;
  border: none;
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
  cursor: pointer;
  text-decoration: none;
  padding: 0 8px;
  margin: 0;
`

const ListingImages = ({
  listingLinks,
  unitVideos,
  unitImages,
  building,
  unit,
  unitType,
  getBedCount,
  isStreetNumberHidden,
  isListingLocked,
  isTabletPortrait,
  isSidePanelHidden,
  sidePanelBreakpoint,
  responsiveMargin
}) => {
  const { t } = useTranslation()

  const { showVideoLink, show3dTourLink, getTourData } = useTourLinks({
    listingLinks,
    unitVideos
  })
  const tourData = getTourData()
  const videoThumbnailUrl = useVideoThumbnail(tourData.videoTour)
  const tour3dThumbnailUrl = useVideoThumbnail(tourData.threeDTour)

  const [fancyboxRef] = useFancybox({
    Carousel: {
      Video: {
        autoplay: false
      },
      Thumbs: {
        type: 'modern',
        thumbTpl:
          '<button type="button" class="listing-thumb">' +
          '<span class="listing-thumb__overlay"></span>' +
          '<img class="f-thumb" draggable="false" data-lazy-src="{{src}}" alt=""/>' +
          '</button>'
      }
    }
  })

  const heroImages = useMemo(() => {
    if (!unitImages?.length) return []

    return unitImages.map(({ id, aws_s3_key }) => {
      return {
        id,
        large: getImageUrl(aws_s3_key, {
          fit: 'fit-in/1200x800',
          quality: 100
        }),
        thumbnail: getImageUrl(aws_s3_key, {
          fit: 'fit-in/200x150',
          quality: 70
        }),
        locked: getImageUrl(aws_s3_key, {
          type: 'blur',
          fit: 'fit-in/1200x800',
          quality: 30
        })
      }
    })
  }, [unitImages])

  const heroImageAlt = useMemo(() => {
    const { full_street_name, street_name, city, state, name } = building
    const bedCount = getBedCount()
    const type = unitType()
    const buildingName = name ? `${name}, ` : ''
    return isStreetNumberHidden()
      ? `${bedCount} ${type} for Rent on ${street_name}, ${city}, ${state} -`
      : `${bedCount} ${type} for Rent in ${buildingName}${full_street_name}, ${city}, ${state} -`
  }, [building, getBedCount, isStreetNumberHidden, unitType])

  const listingType = useMemo(() => {
    return unit
      ? getListingTypeText(
          {
            unitType: unit.unit_type_txt_id,
            unitScope: unit.unit_type_scope_txt_id,
            unitSubtype: unit.unit_subtype_txt_id
          },
          t
        )
      : ''
  }, [unit, t])

  const photosButtonText =
    show3dTourLink || showVideoLink
      ? t('{{count}} Photo', { count: heroImages.length })
      : t('View all {{count}} photo', { count: heroImages.length })
  const tourButtonText = show3dTourLink ? t('1 Tour') : ''
  const videoButtonText = showVideoLink ? t('1 Video') : ''

  return (
    <>
      {heroImages?.length > 0 && (
        <Box>
          <Box
            ref={fancyboxRef}
            id="listing-images-carousel"
            style={{
              position: 'relative',
              width: '100%',
              height: `${
                isTabletPortrait
                  ? structure.desktop.heroImageHeight
                  : structure.mobile.heroImageHeight
              }px`,
              background: colors.background,
              boxShadow: isSidePanelHidden ? 'none' : '#f0f0f0 0px 0px 0px 1px',
              borderRadius: isSidePanelHidden ? '0px' : '10px',
              border: isSidePanelHidden ? 'none' : `1px solid ${colors.grey88}`,
              overflow: 'hidden',
              boxSizing: 'content-box'
            }}
          >
            <Box
              paddingX="xxxsmall"
              paddingY="xxsmall"
              borderRadius="full"
              border="borderNeutralLight"
              background="white"
              boxShadow="small"
              position="absolute"
              display="flex"
              alignItems="center"
              style={{
                height: '32px',
                width: 'max-content',
                bottom: vars.space.small,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 100,
                pointerEvents: 'none'
              }}
            >
              <Text size="xsmall" weight="medium">
                <CarouselLightboxButton>
                  {photosButtonText}
                </CarouselLightboxButton>
              </Text>
              {show3dTourLink && (
                <>
                  <Text size="xsmall" weight="medium">
                    |
                  </Text>
                  <Text size="xsmall" weight="medium">
                    <CarouselLightboxButton>
                      {tourButtonText}
                    </CarouselLightboxButton>
                  </Text>
                </>
              )}
              {showVideoLink && (
                <>
                  <Text size="xsmall" weight="medium">
                    |
                  </Text>
                  <Text size="xsmall" weight="medium">
                    <CarouselLightboxButton>
                      {videoButtonText}
                    </CarouselLightboxButton>
                  </Text>
                </>
              )}
            </Box>
            {heroImages.map((image, i) => (
              <a
                data-fancybox="gallery"
                key={image.id}
                href={isListingLocked ? image.locked : image.large}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: heroImages.length - i
                }}
              >
                {/* images here serve as thumbnails in gallery mode, however the
                  first image is also served as the hero image hence the check */}
                <Image
                  priority
                  src={
                    isListingLocked
                      ? image.locked
                      : i === 0
                      ? image.large
                      : image.thumbnail
                  }
                  alt={heroImageAlt}
                  fill
                  sizes={`(max-width: ${sidePanelBreakpoint}) 100vw, 66vw`}
                  style={{
                    objectFit: 'cover',
                    objectPosition: 'center'
                  }}
                />
              </a>
            ))}
            {tourData.threeDTour.should_embed && (
              <a
                data-fancybox="gallery"
                href={tourData.threeDTour.embed_url}
                data-width="100%"
                data-height="100%"
                data-type={
                  tourData.threeDTour.embed_type === 'iframe'
                    ? 'iframe'
                    : undefined
                }
                style={{
                  position: 'relative',
                  display: 'block',
                  width: '100%',
                  height: '100%'
                }}
                data-thumb-class="fancybox-embedded-video-thumbnail"
              >
                {tourData.threeDTour.embed_type === 'iframe' && (
                  <Image
                    src={tour3dThumbnailUrl || videoPlaceholder}
                    alt="3D Tour"
                    fill
                    sizes="100%"
                    style={{
                      objectFit: 'cover',
                      objectPosition: 'center'
                    }}
                  />
                )}
                {tourData.threeDTour.embed_type === 'video' && (
                  <Image
                    src={videoThumbnailUrl || videoPlaceholder}
                    alt="3D Video Tour"
                    fill
                    sizes="100%"
                    style={{
                      objectFit: 'cover',
                      objectPosition: 'center'
                    }}
                  />
                )}
              </a>
            )}
            {showVideoLink && (
              <a
                data-fancybox="gallery"
                href={tourData.videoTour.link_url}
                data-width="100%"
                data-height="100%"
                style={{
                  position: 'relative',
                  display: 'block',
                  width: '100%',
                  height: '100%'
                }}
                data-thumb-class="fancybox-uploaded-video-thumbnail"
              >
                <Image
                  src={videoThumbnailUrl || videoPlaceholder}
                  alt="Video Tour"
                  fill
                  sizes="100%"
                  style={{
                    objectFit: 'cover',
                    objectPosition: 'center'
                  }}
                />
              </a>
            )}
          </Box>
          <Box
            marginTop="xsmall"
            style={{
              margin: responsiveMargin,
              paddingTop: '10px'
            }}
          >
            <Badge tone="neutral">{listingType}</Badge>
          </Box>
        </Box>
      )}
    </>
  )
}

export default ListingImages
